import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  id: string;
  name: string;
  type: 'vitamin' | 'nad';
  ivCount: string;
  price: string;
  period: string;
  features: string[];
  discountedPrice?: string;
  addedAt: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'addedAt'>) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  getCartTotal: (applyDiscount: boolean) => number;
  itemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'iv_hydration_cart';

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToCart = (item: Omit<CartItem, 'addedAt'>) => {
    const existingItem = items.find(i => i.id === item.id);
    if (existingItem) {
      return;
    }
    
    const newItem: CartItem = {
      ...item,
      addedAt: Date.now(),
    };
    
    setItems(prev => [...prev, newItem]);
  };

  const removeFromCart = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const getCartTotal = (applyDiscount: boolean): number => {
    return items.reduce((total, item) => {
      const priceStr = applyDiscount && item.discountedPrice ? item.discountedPrice : item.price;
      const price = parseFloat(priceStr.replace(/[$,]/g, ''));
      return total + price;
    }, 0);
  };

  const itemCount = items.length;

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, clearCart, getCartTotal, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}